import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Receipt,
  Truck,
  Package,
  CheckCircle,
  Hoodie,
  ChatCircleDots,
  XCircle,
  Star,
  Printer,
} from "@phosphor-icons/react";
import {
  myPurchases,
  mySales,
  shipOrder,
  deliverOrder,
  cancelOrder,
  postReview,
  formatPrice,
  imageSrc,
} from "../lib/api";
import ShippingLabelModal from "../components/ShippingLabelModal";

const StatusBadge = ({ s }) => {
  const map = {
    paid: "bg-[#FFDF85]",
    shipped: "bg-[#D4C4FB]",
    delivered: "bg-[#B4F0A7]",
    cancelled: "bg-[#FFA0A0]",
    pending: "bg-[#EBE8E0]",
  };
  return <span className={`nb-badge ${map[s] || "bg-white"}`}>{s}</span>;
};

const ReviewModal = ({ open, onClose, onSubmit }) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="nb-card w-full max-w-md bg-white">
        <span className="nb-label">Leave a review</span>
        <h3 className="mt-2 font-display text-2xl font-black tracking-tight">How was it?</h3>
        <div className="mt-4 flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <button
              key={i}
              data-testid={`star-${i}`}
              onClick={() => setRating(i)}
              type="button"
            >
              <Star size={32} weight={i <= rating ? "fill" : "regular"} />
            </button>
          ))}
        </div>
        <textarea
          data-testid="review-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={4}
          className="nb-input mt-4 resize-y"
          placeholder="Tell other buyers about the seller… (optional)"
        />
        <div className="mt-5 flex gap-3">
          <button data-testid="review-submit" onClick={() => onSubmit(rating, comment)} className="nb-btn flex-1">
            Submit
          </button>
          <button onClick={onClose} className="nb-btn nb-btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const OrderRow = ({ o, side, onShip, onDeliver, onCancel, onReview, onLabel }) => (
  <div data-testid={`order-row-${o.id}`} className="nb-card bg-white">
    <div className="flex flex-col gap-4 sm:flex-row">
      <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border-[3px] border-[#0A0A0A] bg-[#FDFBF7]">
        {imageSrc(o) ? (
          <img src={imageSrc(o)} alt={o.item_title} className="h-full w-full object-cover" />
        ) : (
          <Hoodie weight="duotone" size={32} />
        )}
      </div>
      <div className="flex-1">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <Link to={`/listing/${o.item_id}`} className="font-display text-lg font-black tracking-tight hover:underline">
              {o.item_title}
            </Link>
            <div className="mt-1 text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">
              {side === "purchase" ? `Seller: @${o.seller_name}` : `Buyer: @${o.buyer_name}`} ·{" "}
              {new Date(o.created_at).toLocaleDateString()}
            </div>
          </div>
          <StatusBadge s={o.status} />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm font-medium sm:grid-cols-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">Qty</div>
            <div className="font-display font-bold">{o.quantity}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">Unit</div>
            <div className="font-display font-bold">{formatPrice(o.unit_price)}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">Shipping</div>
            <div className="font-display font-bold flex items-center gap-1">
              <Truck size={14} /> {o.courier_name}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">Total</div>
            <div className="font-display text-lg font-black">{formatPrice(o.total)}</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to={`/orders/${o.id}/chat`}
            data-testid={`chat-${o.id}`}
            className="nb-btn nb-btn-ghost inline-flex items-center gap-2"
          >
            <ChatCircleDots weight="bold" size={16} /> Message
          </Link>
          {side === "sale" && o.status === "paid" && (
            <>
              <button data-testid={`label-${o.id}`} onClick={() => onLabel(o.id)} className="nb-btn nb-btn-accent inline-flex items-center gap-2">
                <Printer weight="bold" size={16} /> Shipping label
              </button>
              <button data-testid={`ship-${o.id}`} onClick={() => onShip(o.id)} className="nb-btn nb-btn-secondary inline-flex items-center gap-2">
                <Truck weight="bold" size={16} /> Mark shipped
              </button>
            </>
          )}
          {side === "sale" && o.status === "shipped" && (
            <button data-testid={`label-${o.id}`} onClick={() => onLabel(o.id)} className="nb-btn nb-btn-ghost inline-flex items-center gap-2">
              <Printer weight="bold" size={16} /> View label
            </button>
          )}
          {side === "purchase" && (o.status === "shipped" || o.status === "paid") && (
            <button data-testid={`deliver-${o.id}`} onClick={() => onDeliver(o.id)} className="nb-btn inline-flex items-center gap-2">
              <CheckCircle weight="bold" size={16} /> Confirm delivery
            </button>
          )}
          {side === "purchase" && o.status === "delivered" && (
            <button data-testid={`review-${o.id}`} onClick={() => onReview(o)} className="nb-btn nb-btn-secondary inline-flex items-center gap-2">
              <Star weight="bold" size={16} /> Leave review
            </button>
          )}
          {o.status === "paid" && (
            <button data-testid={`cancel-${o.id}`} onClick={() => onCancel(o.id)} className="nb-btn nb-btn-danger inline-flex items-center gap-2">
              <XCircle weight="bold" size={16} /> Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default function Orders() {
  const [sp] = useSearchParams();
  const [tab, setTab] = useState("purchases");
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [labelOrderId, setLabelOrderId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([myPurchases(), mySales()]);
      setPurchases(p);
      setSales(s);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (sp.get("highlight")) load();
  }, [sp]);

  const onShip = async (id) => {
    try {
      await shipOrder(id);
      toast.success("Marked shipped");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const onDeliver = async (id) => {
    try {
      await deliverOrder(id);
      toast.success("Marked delivered — leave a review!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const onCancel = async (id) => {
    if (!window.confirm("Cancel this order? Stock will be restored.")) return;
    try {
      await cancelOrder(id);
      toast.success("Order cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  const submitReview = async (rating, comment) => {
    try {
      await postReview(reviewing.id, rating, comment);
      toast.success("Thanks for the review!");
      setReviewing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    }
  };

  const data = tab === "purchases" ? purchases : sales;

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 sm:px-10">
      <span className="nb-label">Orders</span>
      <h1 className="mt-2 font-display text-4xl font-black tracking-tighter sm:text-5xl">Your buys & sells.</h1>

      <div className="mt-8 flex gap-3">
        <button data-testid="tab-purchases" onClick={() => setTab("purchases")} className={`nb-badge cursor-pointer ${tab === "purchases" ? "bg-[#D4C4FB]" : "bg-white"}`}>
          <Package size={14} weight="bold" /> Purchases ({purchases.length})
        </button>
        <button data-testid="tab-sales" onClick={() => setTab("sales")} className={`nb-badge cursor-pointer ${tab === "sales" ? "bg-[#B4F0A7]" : "bg-white"}`}>
          <Receipt size={14} weight="bold" /> Sales ({sales.length})
        </button>
      </div>

      <div className="mt-6 space-y-4">
        {loading ? (
          <div className="nb-card bg-white">Loading…</div>
        ) : data.length === 0 ? (
          <div className="nb-card bg-[#FDFBF7]" style={{ borderStyle: "dashed" }}>
            <div className="flex items-center gap-3">
              <CheckCircle weight="duotone" size={28} />
              <div>
                <div className="font-display text-lg font-black">No {tab === "purchases" ? "purchases" : "sales"} yet</div>
                <div className="text-sm font-medium text-[#4A4A4A]">
                  {tab === "purchases" ? "Browse the marketplace to find something you love." : "Scan an item and list it to start selling."}
                </div>
              </div>
            </div>
          </div>
        ) : (
          data.map((o) => (
            <OrderRow
              key={o.id}
              o={o}
              side={tab === "purchases" ? "purchase" : "sale"}
              onShip={onShip}
              onDeliver={onDeliver}
              onCancel={onCancel}
              onReview={setReviewing}
              onLabel={setLabelOrderId}
            />
          ))
        )}
      </div>

      <ReviewModal open={!!reviewing} onClose={() => setReviewing(null)} onSubmit={submitReview} />
      {labelOrderId && (
        <ShippingLabelModal orderId={labelOrderId} onClose={() => setLabelOrderId(null)} />
      )}
    </div>
  );
}
